import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCalendar, FiBook, FiInfo, FiMonitor } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export default function StudentCourseView() {
    const { classId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const loadCourseData = async () => {
            if (!currentUser) return;
            try {
                const data = await fetchAPI(`/student/get_class_details.php?class_id=${classId}&user_id=${currentUser.id}`);
                setCourseData(data);
            } catch (err) {
                console.error("Failed to load course details", err);
            } finally {
                setLoading(false);
            }
        };
        loadCourseData();
    }, [classId, currentUser]);

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading course details...</div>;

    if (!courseData) {
        return (
            <div className="p-12 text-center bg-white rounded-xl border border-gray-100 shadow-sm max-w-2xl mx-auto mt-12">
                <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <FiInfo size={40} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Class Not Found</h2>
                <p className="text-gray-500 mb-8 font-medium">
                    We couldn't find the class you're looking for (ID: {classId}).
                    This might be due to a stale link or an old database record.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                        onClick={() => navigate(-1)}
                        className="px-8 py-3 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition-colors uppercase text-sm"
                    >
                        Go Back
                    </button>
                    <button
                        onClick={() => navigate('/dashboard/courses')}
                        className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-100 uppercase text-sm"
                    >
                        Browse Teachers
                    </button>
                </div>
            </div>
        );
    }

    const { months } = courseData;

    // Helper to check if a month is unlocked (Approved OR before 15th of that month)
    const isMonthUnlocked = (month) => {
        if (month.payment_status === 'Approved') return true;

        const now = new Date();
        const currentDay = now.getDate();
        const currentMonthName = now.toLocaleString('default', { month: 'long' });
        const graceDay = parseInt(courseData.grace_period_day || 15);

        // If it's the current month and before or on the grace day
        if (currentDay <= graceDay && (month.name === currentMonthName || month.name.startsWith(currentMonthName + ' '))) {
            return true;
        }

        return false;
    };

    return (
        <div className="space-y-6">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 rounded-xl p-6 md:p-8 text-white relative overflow-hidden">
                <div className="relative z-10">
                    <h1 className="text-3xl font-bold mb-4">{courseData.name}</h1>
                    <div className="flex flex-wrap gap-6 text-sm opacity-90 font-medium">
                        <span className="flex items-center gap-2">• Grade: {courseData.grade}</span>
                        <span className="flex items-center gap-2">• Fee: LKR {courseData.fee}</span>
                        <span className="flex items-center gap-2">• Type: {courseData.type}</span>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">

                {/* Left: Months Grid */}
                <div className="flex-1">
                    <div className="flex items-center gap-4 mb-6 border-b border-gray-200">
                        <button className="px-4 py-2 border-b-2 border-blue-600 text-blue-600 font-bold text-sm">2025</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {months.map((month) => {
                            const unlocked = isMonthUnlocked(month);
                            const isGrace = unlocked && month.payment_status !== 'Approved';

                            return (
                                <div key={month.id} className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all relative overflow-hidden">
                                    {month.payment_status === 'Pending' && (
                                        <div className="absolute top-0 right-0 bg-yellow-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-bl-md z-10 animate-pulse">
                                            Pending Approval
                                        </div>
                                    )}
                                    {month.payment_status === 'Approved' && (
                                        <div className="absolute top-0 right-0 bg-green-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-bl-lg z-10">
                                            Unlocked
                                        </div>
                                    )}
                                    {isGrace && (
                                        <div className="absolute top-0 right-0 bg-blue-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-bl-lg z-10 animate-pulse">
                                            Grace Period Access
                                        </div>
                                    )}

                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <h3 className="font-bold text-gray-900">{month.name}</h3>
                                            <div className="flex items-center gap-2 mt-2">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${month.color} bg-opacity-20`}>
                                                    <FiBook size={14} />
                                                </div>
                                                <span className="text-xs text-gray-500 font-medium">{month.lessons} Lessons</span>
                                            </div>
                                        </div>
                                        <FiInfo className="text-gray-300" />
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mt-4">
                                        <button
                                            disabled={!unlocked}
                                            onClick={() => navigate(`/dashboard/courses/class/${classId}/lesson/${month.id}`)}
                                            className={`py-2 text-white text-xs font-bold rounded transition-colors ${unlocked ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed uppercase'}`}
                                        >
                                            {unlocked ? 'VIEW' : 'LOCKED'}
                                        </button>
                                        <button
                                            onClick={() => navigate('/dashboard/payments', { state: { classId: classId, monthName: month.name } })}
                                            disabled={month.payment_status === 'Approved' || month.payment_status === 'Pending'}
                                            className={`py-2 text-xs font-bold rounded transition-colors ${month.payment_status === 'Approved' || month.payment_status === 'Pending' ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                                        >
                                            GET MONTH
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right: Payment Sidebar - Hidden on Mobile */}
                <div className="block lg:w-80 space-y-6">
                    <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm sticky top-6">
                        <div className="relative w-full aspect-video rounded-lg mb-8 overflow-hidden shadow-2xl shadow-blue-100 group">
                            <img src="../../hero/banner.jpeg" alt="Course Banner" className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                        </div>

                        <div className="bg-red-50 text-red-600 text-xs font-bold py-1 px-2 rounded mb-4 inline-block">

                        </div>

                        <div className="space-y-3 mb-8">


                            <div className="bg-indigo-50/50 p-4 rounded-2xl flex items-center gap-4 border border-indigo-100/30">
                                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-indigo-600 shadow-sm">
                                    <FiInfo size={18} />
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-black text-indigo-400">Class Type</p>
                                    <p className="text-sm font-bold text-gray-900">Online Session</p>
                                </div>
                            </div>

                            <div className="bg-emerald-50/50 p-4 rounded-2xl flex items-center gap-4 border border-emerald-100/30">
                                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-emerald-600 shadow-sm">
                                    <span className="font-bold text-xs">LKR</span>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-wider font-black text-emerald-400">Investment</p>
                                    <p className="text-sm font-bold text-gray-900">{courseData.fee} / Per Month</p>
                                </div>
                            </div>
                        </div>

                        {/* Motivational Section */}
                        <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-2xl mb-8 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/10 rounded-full -mr-10 -mt-10 blur-2xl"></div>
                            <h4 className="text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-2">Success Tip</h4>
                            <p className="text-white text-sm font-medium leading-relaxed italic">
                                "The only way to learn mathematics is to <span className="text-blue-400 font-bold">do mathematics</span>."
                            </p>
                            <div className="mt-4 flex items-center gap-2">
                                <div className="w-4 h-0.5 bg-blue-500"></div>
                                <span className="text-[9px] text-gray-400 font-bold tracking-widest uppercase">Paul Halmos</span>
                            </div>
                        </div>

                        <button className="w-full py-4 bg-gradient-to-r from-blue-700 to-blue-600 text-white font-bold rounded-2xl shadow-xl shadow-blue-200/50 mb-3 hover:shadow-2xl hover:translate-y--0.5 transition-all active:scale-95 group flex items-center justify-center gap-2">
                            <span className="uppercase tracking-widest text-[11px]">Enroll Now</span>
                            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                                <FiBook size={12} />
                            </div>
                        </button>
                        <p className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-tighter mb-8">Secure your spot today</p>

                        <div className="border-t border-gray-100 pt-4">
                            <h4 className="text-xs font-bold text-gray-400 uppercase mb-3">Teacher</h4>
                            <div className="flex items-center gap-3">
                                <img src="https://www.freepik.com/free-photo/liquid-marbling-paint-texture-background-fluid-painting-abstract-texture-intensive-color-mix-wallpaper_26543298.htm#fromView=search&page=1&position=4&uuid=d6b7656d-1790-4c97-ab60-0f508298f9ab&query=backgrod" alt="Teacher" className="w-10 h-10 rounded-full" />
                                <div>
                                    <p className="text-sm font-bold text-gray-900">Manoj</p>
                                    <button className="text-xs text-blue-600 font-bold uppercase mt-1">View Teacher</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
